import { computed, ref, shallowRef, watch } from 'vue'
import { defineStore } from 'pinia'
import { api, type LinksQuery } from '@/api/client'
import type {
  BBox,
  CollectionMeta,
  FeatureCollection,
  Frame,
  Health,
  HistoryBucket,
  LinkKind,
  LinkProfile,
  LinkProperties,
  NodeDetail,
  NodeProperties,
  ServerConfig,
} from '@/api/types'
import { DEFAULT_SNR_THRESHOLDS, type SnrPalette } from '@/styles/scale'
import { isAsymmetric, isFunctional, type ViewMode } from '@/map/lanes'
import { NODE_ISSUES, nodeIssues, type NodeIssue } from '@/map/nodeIssues'
import { CLOCK_OFFSET_MAX_SEC, clockOff } from '@/map/clock'
import { historySpan } from '@/utils/history'

const EMPTY = <P,>(): FeatureCollection<P> => ({ type: 'FeatureCollection', features: [] })

const VIEW_KEY = 'meshqual.view'

interface ViewPrefs {
  mode: ViewMode
  asymOnly: boolean
  palette: SnrPalette
  altitude: boolean
  /** The "Rôle de pont" map layer. */
  bridge: boolean
  /** Node issues the ⚠ flags; the legend switches each off. */
  issues: NodeIssue[]
  /** The clock beside nodes whose clock is off. */
  clock: boolean
}

/** View preferences are per browser; storage may be unavailable, so it is optional. */
function loadView(): ViewPrefs {
  try {
    const v = JSON.parse(localStorage.getItem(VIEW_KEY) ?? '{}') as Partial<Record<keyof ViewPrefs, unknown>>
    return {
      mode: v.mode === 'asymmetry' || v.mode === 'functional' ? v.mode : 'quality',
      asymOnly: v.asymOnly === true,
      palette: v.palette === 'traffic' ? 'traffic' : 'blue',
      altitude: v.altitude === true,
      bridge: v.bridge === true,
      issues: Array.isArray(v.issues)
        ? NODE_ISSUES.filter((i) => (v.issues as unknown[]).includes(i))
        : [...NODE_ISSUES],
      clock: v.clock !== false,
    }
  } catch {
    return {
      mode: 'quality',
      asymOnly: false,
      palette: 'blue',
      altitude: false,
      bridge: false,
      issues: [...NODE_ISSUES],
      clock: true,
    }
  }
}

const ANTENNA_KEY = 'meshqual.antennas'

function loadAntennas(): Record<string, number> {
  try {
    const v = JSON.parse(localStorage.getItem(ANTENNA_KEY) ?? '{}') as Record<string, unknown>
    const out: Record<string, number> = {}
    for (const [k, m] of Object.entries(v)) if (typeof m === 'number' && m >= 0 && m <= 300) out[k] = m
    return out
  } catch {
    return {}
  }
}

export const useMeshStore = defineStore('mesh', () => {
  // shallowRef: these collections are replaced wholesale and handed straight to
  // MapLibre. Deep reactivity over tens of thousands of coordinates would cost
  // far more than it buys.
  const links = shallowRef<FeatureCollection<LinkProperties>>(EMPTY())
  const nodes = shallowRef<FeatureCollection<NodeProperties>>(EMPTY())
  const meta = ref<CollectionMeta | null>(null)

  const config = ref<ServerConfig | null>(null)
  const health = ref<Health | null>(null)

  const selectedLinkId = ref<string | null>(null)
  // A node and a link are never selected together: the panel shows one or the other.
  const selectedNodeKey = ref<string | null>(null)
  const nodeDetail = ref<NodeDetail | null>(null)
  const frames = ref<Frame[]>([])
  const history = ref<HistoryBucket[]>([])
  /**
   * Line of sight of the selected link, fetched as soon as it is selected so
   * the panel's section is ready when unfolded. 'error' covers a disabled
   * feature or an unreachable elevation service.
   */
  const profile = ref<{ state: 'loading' | 'ready' | 'error'; data?: LinkProfile } | null>(null)

  const bbox = ref<BBox | null>(null)
  const kinds = ref<LinkKind[]>(['measured', 'trace', 'topology'])
  const minSamples = ref(1)

  const savedView = loadView()
  const viewMode = ref<ViewMode>(savedView.mode)
  const asymOnly = ref(savedView.asymOnly)
  const palette = ref<SnrPalette>(savedView.palette)
  const altitude = ref(savedView.altitude)
  const bridge = ref(savedView.bridge)
  const issues = ref<NodeIssue[]>(savedView.issues)
  const clock = ref(savedView.clock)
  watch([viewMode, asymOnly, palette, altitude, issues, bridge, clock], ([mode, only, pal, alt, iss, br, clk]) => {
    try {
      localStorage.setItem(
        VIEW_KEY,
        JSON.stringify({ mode, asymOnly: only, palette: pal, altitude: alt, issues: iss, bridge: br, clock: clk }),
      )
    } catch {}
  })
  function toggleIssue(i: NodeIssue) {
    issues.value = issues.value.includes(i) ? issues.value.filter((x) => x !== i) : [...issues.value, i]
  }
  // The CSS ramp variables (legend, frame table) switch on this attribute; the
  // map gets the same palette through its own props.
  watch(
    palette,
    (p) => {
      if (p === 'traffic') document.documentElement.setAttribute('data-palette', 'traffic')
      else document.documentElement.removeAttribute('data-palette')
    },
    { immediate: true },
  )

  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastRefresh = ref<Date | null>(null)

  const snrThresholds = computed<number[]>(
    () => config.value?.snrThresholds ?? [...DEFAULT_SNR_THRESHOLDS],
  )
  const framesMax = computed(() => config.value?.framesMax ?? 20)
  const pushIntervalMs = computed(() => config.value?.pushIntervalMs ?? 2000)
  const asymmetryThresholdDb = computed(() => config.value?.asymmetryThresholdDb ?? 6)
  const measureRetentionSec = computed(() => config.value?.measureRetentionSec ?? 14 * 86_400)
  const clockOffsetMaxSec = computed(() => config.value?.clockOffsetMaxSec ?? CLOCK_OFFSET_MAX_SEC)
  /** Links shown by the "Fonctionnel" view: weaker direction at or above the usable threshold. */
  const functionalCount = computed(
    () => links.value.features.filter((f) => isFunctional(f.properties, snrThresholds.value)).length,
  )
  /**
   * Companions are personal devices, not the mesh: hidden by default with
   * their links, shown while toggled on (key C). Not remembered on purpose.
   */
  const showCompanions = ref(false)
  /** Keys shown whatever the toggle: the companion driving the trace panel. */
  const alwaysShown = ref<string[]>([])
  const hidden = (key: string, type?: string) => type === 'companion' && !alwaysShown.value.includes(key)
  const visibleNodes = computed(() =>
    showCompanions.value
      ? nodes.value
      : { ...nodes.value, features: nodes.value.features.filter((f) => !hidden(f.properties.key, f.properties.nodeType)) },
  )
  const visibleLinks = computed(() =>
    showCompanions.value
      ? links.value
      : {
          ...links.value,
          features: links.value.features.filter(
            (f) => !hidden(f.properties.aKey, f.properties.aType) && !hidden(f.properties.bKey, f.properties.bType),
          ),
        },
  )
  const isCompanion = (t?: string) => t === 'companion'
  const companionCount = computed(() => nodes.value.features.filter((f) => isCompanion(f.properties.nodeType)).length)
  /** Nodes in view per bridge level, for the layer's legend. */
  const bridgeCounts = computed(() => {
    const c: Record<string, number> = { low: 0, medium: 0, high: 0, critical: 0 }
    for (const f of visibleNodes.value.features) {
      const l = f.properties.bridge
      if (l) c[l] = (c[l] ?? 0) + 1
    }
    return c
  })
  /** Listen-only observers in view (drawn as diamonds). */
  const listenOnlyCount = computed(() => nodes.value.features.filter((f) => f.properties.listenOnly).length)
  /** Nodes in view per configuration issue (flagged ⚠ on the map). */
  const issueCounts = computed(() => {
    const c: Record<NodeIssue, number> = { hash1: 0, noRegion: 0 }
    for (const f of nodes.value.features) for (const i of nodeIssues(f.properties)) c[i]++
    return c
  })
  /** Nodes in view whose clock is off (the clock beside them on the map). */
  const clockCount = computed(
    () => nodes.value.features.filter((f) => clockOff(f.properties.clockOffsetSec, clockOffsetMaxSec.value)).length,
  )
  const asymmetricCount = computed(
    () => links.value.features.filter((f) => isAsymmetric(f.properties, asymmetryThresholdDb.value)).length,
  )

  /**
   * The selected link with every property (fields=full): the map's own
   * collection only carries what the map reads. Reloaded when the live stream
   * reports the link changed.
   */
  const selectedFull = shallowRef<LinkProperties | null>(null)
  const selectedLink = computed(() =>
    selectedLinkId.value && selectedFull.value?.linkId === selectedLinkId.value ? selectedFull.value : null,
  )

  async function loadSelectedFull(linkId: string) {
    try {
      const r = await api.links({ ids: [linkId], full: true })
      if (selectedLinkId.value !== linkId) return
      selectedFull.value = r.features?.[0]?.properties ?? null
    } catch {
      // The panel keeps what it had; the next update retries.
    }
  }

  /** Links the server could not draw because an endpoint never advertised a position. */
  const undrawable = computed(() => meta.value?.withoutPosition ?? 0)

  let linksAbort: AbortController | null = null

  async function loadConfig() {
    try {
      config.value = await api.config()
    } catch (e) {
      error.value = describe(e)
    }
  }

  async function refreshLinks() {
    linksAbort?.abort()
    const ac = new AbortController()
    linksAbort = ac
    loading.value = true
    try {
      const q: LinksQuery = {
        bbox: bbox.value,
        kinds: kinds.value,
        minSamples: minSamples.value,
      }
      const [l, n] = await Promise.all([
        api.links(q, ac.signal),
        api.nodes(bbox.value, ac.signal),
      ])
      links.value = { ...l, features: l.features ?? [] }
      nodes.value = { ...n, features: n.features ?? [] }
      if (selectedLinkId.value) void loadSelectedFull(selectedLinkId.value)
      meta.value = l.meta ?? null
      lastRefresh.value = new Date()
      error.value = null
    } catch (e) {
      if (!isAbort(e)) error.value = describe(e)
    } finally {
      if (linksAbort === ac) loading.value = false
    }
  }

  /**
   * Refetches only the links the live stream reported changed and merges them
   * in. Unchanged links keep their very objects, which is how the map tells
   * what to redraw (useMapLibre setLinks). Links gone from the answer (evicted,
   * filtered, out of view) are dropped.
   */
  async function patchLinks(ids: string[]) {
    if (!ids.length) return
    if (!bbox.value || !links.value.features.length) return refreshLinks()
    try {
      const r = await api.links({ bbox: bbox.value, kinds: kinds.value, minSamples: minSamples.value, ids })
      const fresh = new Map((r.features ?? []).map((f) => [f.properties.linkId, f]))
      const asked = new Set(ids)
      const kept = links.value.features
        .filter((f) => !asked.has(f.properties.linkId) || fresh.has(f.properties.linkId))
        .map((f) => fresh.get(f.properties.linkId) ?? f)
      const known = new Set(kept.map((f) => f.properties.linkId))
      const added = [...fresh.values()].filter((f) => !known.has(f.properties.linkId))
      links.value = { ...links.value, features: [...kept, ...added] }
      if (selectedLinkId.value && asked.has(selectedLinkId.value)) void loadSelectedFull(selectedLinkId.value)
    } catch (e) {
      if (!isAbort(e)) error.value = describe(e)
    }
  }

  async function refreshHealth() {
    try {
      health.value = await api.health()
    } catch {
      // Health is informational; a failure here must not blank the map.
    }
  }

  async function selectNode(key: string | null) {
    selectedNodeKey.value = key
    nodeDetail.value = null
    if (key) {
      selectedLinkId.value = null
      frames.value = []
      history.value = []
      await loadNode(key)
    }
  }

  async function loadNode(key: string) {
    try {
      const d = await api.node(key)
      if (selectedNodeKey.value === key) nodeDetail.value = d
    } catch (e) {
      if (!isAbort(e)) error.value = describe(e)
    }
  }

  /**
   * Antenna heights the user gave, per node key, in metres above ground. Kept
   * per browser: masts do not move, and the server only knows a default.
   */
  const antennas = ref<Record<string, number>>(loadAntennas())
  let profileSeq = 0
  let antennaTimer = 0

  async function loadProfile(linkId: string) {
    const seq = ++profileSeq
    // A height change keeps the chart up while the new one computes.
    if (profile.value?.state !== 'ready') profile.value = { state: 'loading' }
    const [a, b] = linkId.split(':')
    try {
      const data = await api.profile(linkId, { antA: antennas.value[a!], antB: antennas.value[b!] })
      if (seq === profileSeq && selectedLinkId.value === linkId) profile.value = { state: 'ready', data }
    } catch {
      if (seq === profileSeq && selectedLinkId.value === linkId) profile.value = { state: 'error' }
    }
  }

  /** Sets a node's antenna height (null: back to the default) and redraws the profile. */
  function setAntenna(key: string, metres: number | null) {
    const next = { ...antennas.value }
    if (metres === null || !Number.isFinite(metres)) delete next[key]
    else next[key] = Math.max(0, Math.min(300, metres))
    antennas.value = next
    try {
      localStorage.setItem(ANTENNA_KEY, JSON.stringify(next))
    } catch {}
    const id = selectedLinkId.value
    if (!id || !id.split(':').includes(key)) return
    // Typing 1, 12, 120 should not send three requests.
    window.clearTimeout(antennaTimer)
    antennaTimer = window.setTimeout(() => void loadProfile(id), 350)
  }

  async function selectLink(linkId: string | null) {
    selectedLinkId.value = linkId
    selectedFull.value = null
    if (linkId) void loadSelectedFull(linkId)
    profile.value = null
    if (linkId) void loadProfile(linkId)
    if (linkId) {
      selectedNodeKey.value = null
      nodeDetail.value = null
    }
    frames.value = []
    history.value = []
    if (!linkId) return
    const span = historySpan(measureRetentionSec.value)
    await Promise.all([loadFrames(linkId), loadHistory(linkId, span.window, span.bucket)])
  }

  async function loadFrames(linkId: string) {
    try {
      const res = await api.frames(linkId, framesMax.value)
      if (selectedLinkId.value !== linkId) return
      frames.value = res.frames ?? []
    } catch (e) {
      if (!isAbort(e)) error.value = describe(e)
    }
  }

  async function loadHistory(linkId: string, window = '336h', bucket = '4h') {
    try {
      const res = await api.history(linkId, window, bucket)
      if (selectedLinkId.value !== linkId) return
      history.value = res.buckets ?? []
    } catch {
      // No database configured means no history; the live panel still works.
      history.value = []
    }
  }

  /** Prepend a frame pushed over SSE, keeping the list bounded and newest-first. */
  function pushFrame(linkId: string, frame: Frame) {
    if (linkId !== selectedLinkId.value) return
    if (frames.value.some((f) => f.wireHash === frame.wireHash && f.at === frame.at)) return
    // Like the server: the latest frames, plus the older ones that carried an
    // SNR, so path-hop traffic does not push the measurements out.
    const all = [frame, ...frames.value]
    const max = framesMax.value
    const older = all.slice(max).filter((f) => f.snr !== null && f.snr !== undefined)
    frames.value = [...all.slice(0, max), ...older.slice(0, max)]
  }

  function setBBox(b: BBox) {
    bbox.value = b
  }

  function toggleKind(kind: LinkKind) {
    kinds.value = kinds.value.includes(kind)
      ? kinds.value.filter((k) => k !== kind)
      : [...kinds.value, kind]
  }

  return {
    links,
    nodes,
    meta,
    config,
    health,
    selectedLinkId,
    selectedNodeKey,
    nodeDetail,
    selectedLink,
    patchLinks,
    frames,
    history,
    profile,
    antennas,
    setAntenna,
    bbox,
    kinds,
    minSamples,
    viewMode,
    asymOnly,
    palette,
    altitude,
    bridge,
    bridgeCounts,
    asymmetryThresholdDb,
    measureRetentionSec,
    clockOffsetMaxSec,
    clock,
    clockCount,
    asymmetricCount,
    functionalCount,
    issueCounts,
    listenOnlyCount,
    showCompanions,
    alwaysShown,
    visibleNodes,
    visibleLinks,
    companionCount,
    issues,
    toggleIssue,
    loading,
    error,
    lastRefresh,
    snrThresholds,
    framesMax,
    pushIntervalMs,
    undrawable,
    loadConfig,
    refreshLinks,
    refreshHealth,
    selectLink,
    selectNode,
    loadNode,
    loadFrames,
    loadHistory,
    pushFrame,
    setBBox,
    toggleKind,
  }
})

function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError'
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}
